import React, { useState, FC } from "react";
import { writeFile, utils } from "xlsx";
import { Button, notification, Tooltip } from "antd";
import { DownloadOutlined, FileExcelOutlined } from "@ant-design/icons";
import { axiosInstance } from "@/utilities";
import {
  API_URL_LINKED_IMPORT_ITEMS,
  API_URL_LINKED_IMPORT_FILE,
} from "@/services/XCONS_ENV";
import { t } from "i18next";

interface ErrorDetail {
  rawSku: string;
  errors: Array<{ field: string; message: string }>;
}

interface DownloadButtonProps {
  importId: number;
  fileName?: string;
  errorDetails?: ErrorDetail[];
  reportMode?: boolean;
  fileKey?: string;
}

export const DownloadXlsButton: FC<DownloadButtonProps> = React.memo(
  ({ importId, fileName, errorDetails, reportMode = false, fileKey }) => {
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const fetchAllItems = async () => {
      let allData: any = [];
      let currentPage = 1;
      let totalPages = 1;
      const limitPerPage = 100;

      while (currentPage <= totalPages) {
        const response = await axiosInstance.get(
          `${API_URL_LINKED_IMPORT_ITEMS}/${importId}?page=${currentPage}&limit=${limitPerPage}`,
        );
        const data = response.data?.data || [];
        const meta = response.data?.meta;
        if (data.length) allData = [...allData, ...data];
        totalPages = meta?.totalPages || 1;
        currentPage++;
      }
      return allData;
    };

    const downloadOriginal = async () => {
      setIsLoading(true);
      try {
        if (fileKey) {
          // Descargar archivo original desde S3
          const response = await axiosInstance.get(
            `${API_URL_LINKED_IMPORT_FILE}/${importId}`,
            { responseType: "blob" },
          );
          const url = URL.createObjectURL(new Blob([response.data]));
          const a = document.createElement("a");
          a.href = url;
          a.download = fileName ?? `import_${importId}.xlsx`;
          a.click();
          URL.revokeObjectURL(url);
        } else {
          // Fallback: integración o import sin archivo guardado
          const allData = await fetchAllItems();
          if (allData.length === 0) {
            notification.error({ message: t("message.noDataFound") });
            return;
          }
          const documentName =
            fileName ?? `productos_importados_${importId}.xlsx`;
          const fileNameWithExt = documentName.includes(".")
            ? documentName
            : `${documentName}.xlsx`;
          const worksheet = utils.json_to_sheet(allData);
          const workbook = utils.book_new();
          utils.book_append_sheet(workbook, worksheet, `import_${importId}`);
          writeFile(workbook, fileNameWithExt);
        }
        notification.success({ message: t("message.archiveDownload") });
      } catch (error: any) {
        notification.error({
          message: t("message.archiveDownloadError"),
          description: error.message,
        });
      } finally {
        setIsLoading(false);
      }
    };

    const downloadReport = async () => {
      setIsLoading(true);
      try {
        const allData = await fetchAllItems();

        const motivoDe = (e: {
          errors: Array<{ field: string; message: string }>;
        }) => e.errors.map((err) => `${err.field}: ${err.message}`).join(" | ");
        const errorMap = new Map(
          (errorDetails ?? []).map((e) => [e.rawSku, motivoDe(e)]),
        );
        const dbSkus = new Set(allData.map((item: any) => item.sku));

        const itemRows = allData.map((item: any) => {
          const motivo = errorMap.get(item.sku);
          return motivo
            ? { ...item, estado: "Error", motivo }
            : { ...item, estado: "OK" };
        });

        const validationOnlyErrors = (errorDetails ?? [])
          .filter((e) => !dbSkus.has(e.rawSku))
          // El motivo de **cada** error, no el del mapa: las filas sin SKU comparten el
          // "(sin SKU)" y el mapa se quedaba con el último para todas (xcons-docs#609).
          .map((e) => ({
            sku: e.rawSku,
            estado: "Error",
            motivo: motivoDe(e),
          }));

        const combined = [...itemRows, ...validationOnlyErrors];

        if (combined.length === 0) {
          notification.error({ message: t("message.noDataFound") });
          return;
        }

        const baseName = fileName
          ? fileName.replace(/\.[^/.]+$/, "")
          : `import_${importId}`;
        const reportName = `${baseName}_reporte.xlsx`;

        const worksheet = utils.json_to_sheet(combined);
        const workbook = utils.book_new();
        utils.book_append_sheet(workbook, worksheet, `reporte_${importId}`);
        writeFile(workbook, reportName);
        notification.success({ message: t("message.archiveDownload") });
      } catch (error: any) {
        notification.error({
          message: t("message.archiveDownloadError"),
          description: error.message,
        });
      } finally {
        setIsLoading(false);
      }
    };

    if (reportMode) {
      return (
        <Tooltip title="Descargar reporte de estados" placement="topRight">
          <Button
            icon={<FileExcelOutlined />}
            onClick={downloadReport}
            loading={isLoading}
            size="small"
          />
        </Tooltip>
      );
    }

    return (
      <Tooltip title={t("tooltip.archiveDownload")} placement="topRight">
        <Button
          icon={<DownloadOutlined />}
          onClick={downloadOriginal}
          loading={isLoading}
          // Cuadrado fijo: dentro de una fila flex se estiraba al alto del nombre del archivo.
          style={{ width: 32, height: 32, flexShrink: 0 }}
        />
      </Tooltip>
    );
  },
);
